import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, Package, MapPin, User, CreditCard, FileDown } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import { CourierShell, Field, inputCls, C } from '../../components/courier/CourierUI';

const STEPS = ['Package', 'Collection', 'Contacts', 'Payment'];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CourierShip() {
  const nav = useNavigate();
  const { state } = useLocation();
  const [zones, setZones] = useState([]);
  const [services, setServices] = useState([]);
  const [points, setPoints] = useState([]);
  const [step, setStep] = useState(0);
  const [quoteRef, setQuoteRef] = useState(state?.quoteRef || null);
  const [quote, setQuote] = useState(state?.quote || null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const [pkg, setPkg] = useState({ originZone: 'lusaka', destZone: 'copperbelt', serviceLevel: 'express', weightKg: 1, lengthCm: 20, widthCm: 15, heightCm: 10, declaredValue: 0, insured: false, contents: '' });
  const [mode, setMode] = useState('dropoff');
  const [servicePointId, setServicePointId] = useState('');
  const [originAddress, setOriginAddress] = useState('');
  const [destAddress, setDestAddress] = useState('');
  const [pickupWindow, setPickupWindow] = useState('');
  const [sender, setSender] = useState({ name: '', phone: '', email: '' });
  const [receiver, setReceiver] = useState({ name: '', phone: '', email: '' });
  const [customs, setCustoms] = useState({ hsCode: '', description: '', value: '' });
  const [paymentMethod, setPaymentMethod] = useState('mobile_money');

  const originZone = quote?.originZone || pkg.originZone;
  const isIntl = quote?.international ?? (pkg.destZone === 'international' || pkg.destZone === 'africa');

  useEffect(() => {
    api.get('/courier/zones').then(({ data }) => { setZones(data.zones); setServices(data.serviceLevels); });
    if (state?.quoteRef) setStep(1);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (mode === 'dropoff' && originZone) {
      api.get(`/courier/service-points?zone=${originZone}`).then(({ data }) => setPoints(data.servicePoints));
    }
  }, [mode, originZone]);

  const ensureQuote = async () => {
    if (quoteRef) return quoteRef;
    const { data } = await api.post('/courier/quote', {
      originZone: pkg.originZone, destZone: pkg.destZone, serviceLevel: pkg.serviceLevel,
      parcel: { weightKg: Number(pkg.weightKg), lengthCm: Number(pkg.lengthCm), widthCm: Number(pkg.widthCm), heightCm: Number(pkg.heightCm), declaredValue: Number(pkg.declaredValue), insured: pkg.insured, contents: pkg.contents },
    });
    setQuoteRef(data.quoteRef); setQuote(data.quote);
    return data.quoteRef;
  };

  const next = async () => {
    try {
      if (step === 0) { setBusy(true); await ensureQuote(); }
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Could not price parcel.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!sender.name || !sender.phone || !receiver.name || !receiver.phone) {
      toast({ title: 'Missing contacts', description: 'Sender and receiver name + phone are required.', variant: 'destructive' });
      setStep(2); return;
    }
    setBusy(true);
    try {
      const ref = await ensureQuote();
      const { data } = await api.post('/courier/shipments', {
        quoteRef: ref, mode, servicePointId: servicePointId || null, pickupWindow,
        originAddress, destAddress, sender, receiver, paymentMethod,
        customs: isIntl ? customs : null,
      });
      setDone(data.shipment);
    } catch (e) {
      toast({ title: 'Booking failed', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <CourierShell>
        <div className="max-w-xl mx-auto text-center" data-testid="ship-success">
          <div className="h-16 w-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: C.yellow }}>
            <Check className="h-8 w-8 text-black" strokeWidth={3} />
          </div>
          <h1 className="text-3xl font-black">Shipment booked!</h1>
          <p className="text-black/60 mt-1">Your waybill number is</p>
          <p className="font-mono text-2xl font-black my-2" data-testid="ship-awb">{done.awb}</p>
          {done.qr && <img src={done.qr} alt="QR" className="h-32 w-32 mx-auto my-3" />}
          <div className="flex flex-wrap gap-3 justify-center mt-6">
            <a href={`${BACKEND}/api/courier/shipments/${done.id}/label.pdf`} target="_blank" rel="noreferrer"
              data-testid="ship-label-btn" className="px-5 py-3 rounded-xl font-black text-black flex items-center gap-2" style={{ background: C.yellow }}>
              <FileDown className="h-4 w-4" /> Download Label
            </a>
            <button onClick={() => nav(`/courier/track/${done.awb}`)} data-testid="ship-track-btn"
              className="px-5 py-3 rounded-xl font-black border-2 border-black">Track Shipment</button>
          </div>
        </div>
      </CourierShell>
    );
  }

  return (
    <CourierShell>
      <h1 className="text-3xl font-black mb-4">Ship Now</h1>
      {/* Step indicator */}
      <div className="flex items-center mb-8">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex items-center gap-2">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-black ${i <= step ? 'text-black' : 'bg-white text-black/30 border border-black/15'}`} style={i <= step ? { background: C.yellow } : {}}>{i + 1}</div>
              <span className={`text-sm font-bold ${i === step ? 'text-black' : 'text-black/40'}`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className="flex-1 h-0.5 mx-2 bg-black/10" />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-white rounded-2xl p-6 border border-black/5">
        {step === 0 && (
          <div className="space-y-4" data-testid="ship-step-package">
            <div className="grid grid-cols-2 gap-4">
              <Field label="From"><select className={inputCls} value={pkg.originZone} onChange={(e) => setPkg({ ...pkg, originZone: e.target.value })} data-testid="ship-origin">{zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}</select></Field>
              <Field label="To"><select className={inputCls} value={pkg.destZone} onChange={(e) => setPkg({ ...pkg, destZone: e.target.value })} data-testid="ship-dest">{zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}</select></Field>
            </div>
            <Field label="Service">
              <div className="grid grid-cols-2 gap-2">
                {services.map((s) => (
                  <button key={s.id} type="button" onClick={() => setPkg({ ...pkg, serviceLevel: s.id })} className={`p-3 rounded-xl border-2 text-left ${pkg.serviceLevel === s.id ? 'border-black' : 'border-black/10'}`} style={pkg.serviceLevel === s.id ? { background: C.yellow } : {}}>
                    <p className="font-black text-sm">{s.name}</p><p className="text-[11px] text-black/60">{s.etaDomestic}</p>
                  </button>
                ))}
              </div>
            </Field>
            <div className="grid grid-cols-4 gap-3">
              <Field label="Weight kg"><input type="number" className={inputCls} value={pkg.weightKg} onChange={(e) => setPkg({ ...pkg, weightKg: e.target.value })} data-testid="ship-weight" /></Field>
              <Field label="L cm"><input type="number" className={inputCls} value={pkg.lengthCm} onChange={(e) => setPkg({ ...pkg, lengthCm: e.target.value })} /></Field>
              <Field label="W cm"><input type="number" className={inputCls} value={pkg.widthCm} onChange={(e) => setPkg({ ...pkg, widthCm: e.target.value })} /></Field>
              <Field label="H cm"><input type="number" className={inputCls} value={pkg.heightCm} onChange={(e) => setPkg({ ...pkg, heightCm: e.target.value })} /></Field>
            </div>
            <Field label="Contents"><input className={inputCls} value={pkg.contents} onChange={(e) => setPkg({ ...pkg, contents: e.target.value })} data-testid="ship-contents" /></Field>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4" data-testid="ship-step-collection">
            <div className="grid grid-cols-2 gap-3">
              {['dropoff', 'pickup'].map((m) => (
                <button key={m} type="button" onClick={() => setMode(m)} data-testid={`ship-mode-${m}`}
                  className={`p-4 rounded-xl border-2 text-left ${mode === m ? 'border-black' : 'border-black/10'}`} style={mode === m ? { background: C.yellow } : {}}>
                  <MapPin className="h-5 w-5 mb-1" />
                  <p className="font-black">{m === 'dropoff' ? 'Drop-off' : 'Pickup'}</p>
                  <p className="text-[11px] text-black/60">{m === 'dropoff' ? 'Bring to a service point' : 'We collect from you'}</p>
                </button>
              ))}
            </div>
            {mode === 'dropoff' ? (
              <Field label="Service Point">
                <select className={inputCls} value={servicePointId} onChange={(e) => setServicePointId(e.target.value)} data-testid="ship-service-point">
                  <option value="">Select a point…</option>
                  {points.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.city}</option>)}
                </select>
              </Field>
            ) : (
              <>
                <Field label="Pickup Address"><input className={inputCls} value={originAddress} onChange={(e) => setOriginAddress(e.target.value)} data-testid="ship-pickup-address" /></Field>
                <Field label="Preferred Window"><input className={inputCls} value={pickupWindow} onChange={(e) => setPickupWindow(e.target.value)} placeholder="e.g. Tomorrow 09:00–12:00" /></Field>
              </>
            )}
            <Field label="Delivery Address"><input className={inputCls} value={destAddress} onChange={(e) => setDestAddress(e.target.value)} data-testid="ship-dest-address" /></Field>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5" data-testid="ship-step-contacts">
            <div>
              <p className="font-black mb-2 flex items-center gap-1"><User className="h-4 w-4" /> Sender</p>
              <div className="grid grid-cols-3 gap-3">
                <input className={inputCls} placeholder="Name" value={sender.name} onChange={(e) => setSender({ ...sender, name: e.target.value })} data-testid="ship-sender-name" />
                <input className={inputCls} placeholder="Phone" value={sender.phone} onChange={(e) => setSender({ ...sender, phone: e.target.value })} data-testid="ship-sender-phone" />
                <input className={inputCls} placeholder="Email" value={sender.email} onChange={(e) => setSender({ ...sender, email: e.target.value })} />
              </div>
            </div>
            <div>
              <p className="font-black mb-2 flex items-center gap-1"><User className="h-4 w-4" /> Receiver</p>
              <div className="grid grid-cols-3 gap-3">
                <input className={inputCls} placeholder="Name" value={receiver.name} onChange={(e) => setReceiver({ ...receiver, name: e.target.value })} data-testid="ship-receiver-name" />
                <input className={inputCls} placeholder="Phone" value={receiver.phone} onChange={(e) => setReceiver({ ...receiver, phone: e.target.value })} data-testid="ship-receiver-phone" />
                <input className={inputCls} placeholder="Email" value={receiver.email} onChange={(e) => setReceiver({ ...receiver, email: e.target.value })} />
              </div>
            </div>
            {isIntl && (
              <div className="p-4 rounded-xl border border-black/10" data-testid="ship-customs">
                <p className="font-black mb-2 flex items-center gap-1"><Package className="h-4 w-4" /> Customs (International)</p>
                <div className="grid grid-cols-3 gap-3">
                  <input className={inputCls} placeholder="HS Code" value={customs.hsCode} onChange={(e) => setCustoms({ ...customs, hsCode: e.target.value })} data-testid="ship-customs-hs" />
                  <input className={inputCls} placeholder="Description" value={customs.description} onChange={(e) => setCustoms({ ...customs, description: e.target.value })} />
                  <input className={inputCls} placeholder="Value (K)" value={customs.value} onChange={(e) => setCustoms({ ...customs, value: e.target.value })} />
                </div>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="ship-step-payment">
            <Field label="Payment Method">
              <div className="grid grid-cols-2 gap-2">
                {[['mobile_money', 'Mobile Money'], ['card', 'Card'], ['cash', 'Cash on Pickup'], ['cod', 'Cash on Delivery']].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setPaymentMethod(v)} data-testid={`ship-pay-${v}`}
                    className={`p-3 rounded-xl border-2 font-bold text-sm flex items-center gap-2 ${paymentMethod === v ? 'border-black' : 'border-black/10'}`} style={paymentMethod === v ? { background: C.yellow } : {}}>
                    <CreditCard className="h-4 w-4" /> {l}
                  </button>
                ))}
              </div>
            </Field>
            {quote && (
              <div className="flex justify-between items-center p-4 rounded-xl" style={{ background: C.ink }}>
                <span className="text-white/70 text-sm">Total to pay</span>
                <span className="text-white font-black text-2xl" data-testid="ship-total">K{Number(quote.total).toFixed(2)}</span>
              </div>
            )}
            <p className="text-[11px] text-black/40">Payment is collected/confirmed on pickup or drop-off. Mobile Money is currently in test mode.</p>
          </div>
        )}

        {/* Nav buttons */}
        <div className="flex justify-between mt-8">
          <button onClick={() => setStep((s) => Math.max(s - 1, 0))} disabled={step === 0}
            className="px-5 py-3 rounded-xl font-bold border-2 border-black/15 disabled:opacity-30" data-testid="ship-back-btn">Back</button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} disabled={busy} className="px-6 py-3 rounded-xl font-black text-black" style={{ background: C.yellow }} data-testid="ship-next-btn">{busy ? '…' : 'Continue'}</button>
          ) : (
            <button onClick={submit} disabled={busy} className="px-6 py-3 rounded-xl font-black text-black" style={{ background: C.yellow }} data-testid="ship-confirm-btn">{busy ? 'Booking…' : 'Confirm & Book'}</button>
          )}
        </div>
      </div>
    </CourierShell>
  );
}
